'use client';

import { useCallback, useRef, useState } from 'react';
import { useCartStore, useShallow, CustomImage, DotSpinner } from '..';
import styles from './ProductCardM1.module.css';

import CustomLink from 'components/shared/CustomLink';
import { useTranslations } from 'next-intl';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { getTaxString } from 'components/shared/Cart/utils';
import { useClickOutside } from 'hooks/dom/useClickOutside';
import {
  CurrencySymbol,
  getCurrencySymbolFromEnum,
  useFormatPrice,
} from 'hooks/pricing/useFormattedPrice';
import { useRouter } from 'next/navigation';
import { ProductType } from 'components/shared/Cart/types';
import { Currency } from 'utils/Currency';
import { M1ProductData } from 'content/products/m1/IN';
import { ProductPurchaseType } from 'content/products/productMap';
import { isM1UhxBundlingEnabled } from 'content/products/m1/m1UhxBundlingConfig';
import { ultrahumanXDiscountCodes } from 'content/ultrahumanX';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import type { PromoDiscount } from 'lib/pricing/promoDiscount';
import { ONE_TIME_M1_OFFER } from 'lib/commerce/cart/campaignLinePresence';
import { m1PlanSale } from 'lib/pricing/displayedPrice';
import {
  ShopProductCardRoot,
  ShopCardBody,
  ShopCardBottomSheet,
  ShopCardFooterRow,
  ShopCardMediaDimBackdrop,
  ShopCardPlanTileMedia,
  ShopCardPriceCluster,
  ShopCardPrimaryCta,
  ShopCardSpecSection,
  ShopCardTag,
} from './shopProductCard';

export const ProductCardM1 = ({
  id,
  className,
  imgSrc,
  title,
  description,
  price,
  outOfStock,
  plans,
  directCheckout,
  defaultProduct,
  originalPrice,
  discountPercentage,
  promos,
  oneTime = false,
}: {
    id?: string;
    className?: string;
    imgSrc?: string;
    title: string;
    description?: string;
    price: number;
    outOfStock?: boolean;
    plans: { [key in ProductPurchaseType]?: M1ProductData };
    directCheckout: boolean;
    defaultProduct: ProductPurchaseType;
    originalPrice?: string;
    discountPercentage?: string;
    promos?: PromoDiscount[];
    /** One-time plans follow the buy-page campaign and UHX bundle. */
    oneTime?: boolean;
  }) => {
    const CartClient = useCartStore(
      useShallow((s) => ({
        store: s.store,
        taxString: s.taxString,
        currency: s.currency,
        ultrahumanXAdded: s.ultrahumanXAdded,
        addToCart: s.addToCart,
      })),
    );
    const { store: regionStore } = useRegionLocale();
    const resolvedStore = (CartClient.store ?? regionStore) as ShopifyStore;
    const tCart = useTranslations('cart');
    const t = useTranslations('ring');
    const resolvedTaxString =
      CartClient.taxString || getTaxString(resolvedStore, tCart);
    // const [flipCard, setFlipCard] = useState(false);
    const [showPlanSelect, setShowPlanSelect] = useState(false);
    const [selectedPlanKey, setSelectedPlanKey] =
      useState<ProductPurchaseType | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const formatPriceWithComma = useFormatPrice();
    const ref = useRef<HTMLDivElement>(null);
    const router = useRouter();

    const planEntries = Object.entries(plans).filter(([, v]) => v != null) as [
      ProductPurchaseType,
      M1ProductData,
    ][];
    const hasManyPlans = planEntries.length > 1;
    const uhxFraction =
      oneTime &&
      isM1UhxBundlingEnabled(resolvedStore) &&
      CartClient.ultrahumanXAdded
        ? (ultrahumanXDiscountCodes[resolvedStore]?.discount ?? 0)
        : 0;
    const priceFor = (list: number) => {
      if (uhxFraction > 0 || (promos && promos.length > 0)) {
        return m1PlanSale(list, promos, uhxFraction);
      }
      if (originalPrice && discountPercentage) {
        return list * (1 - parseFloat(discountPercentage) / 100);
      }
      return list;
    };
    const quoted = hasManyPlans
      ? planEntries.reduce(
          (best, [, plan]) => {
            const sale = priceFor(plan.price);
            return sale < best.sale ? { list: plan.price, sale } : best;
          },
          {
            list: planEntries[0][1].price,
            sale: priceFor(planEntries[0][1].price),
          },
        )
      : { list: price, sale: priceFor(price) };
    const showStrike = quoted.sale < quoted.list;

    const currency =
      CurrencySymbol[(CartClient.currency ?? Currency.usd) as Currency];

    const closePlanSelect = useCallback(() => {
      setTimeout(() => {
        setShowPlanSelect(false);
        setSelectedPlanKey(null);
      }, 200);
    }, []);

    useClickOutside(ref, closePlanSelect, showPlanSelect);

    const getSelectedPlan = (): M1ProductData | undefined => {
      if (selectedPlanKey) return plans[selectedPlanKey];
      return plans[defaultProduct] ?? planEntries[0]?.[1];
    };

    const handleAddToCart = async () => {
      if (outOfStock) return;
      const plan = getSelectedPlan();
      if (!plan) return;

      if (directCheckout && plan.checkoutUrl) {
        try {
          setIsLoading(true);
          await router.replace(plan.checkoutUrl);
        } finally {
          setIsLoading(false);
        }
      } else {
        try {
          setIsLoading(true);
          await CartClient.addToCart([
            {
              shopifyVariantId: plan.variantId,
              productType: ProductType.M1,
              ...(oneTime
                ? {
                    params: {
                      noteAttributes: [
                        { key: '_m1_offer', value: ONE_TIME_M1_OFFER },
                      ],
                    },
                  }
                : {}),
            },
          ]);
        } finally {
          setIsLoading(false);
          if (showPlanSelect) closePlanSelect();
        }
      }
    };

    const handleButtonClick = () => {
      if (outOfStock) return;
      if (!hasManyPlans) {
        handleAddToCart();
      } else {
        setShowPlanSelect(true);
      }
    };

    return (
      <ShopProductCardRoot
        id={id}
        className={[styles.root, className].filter(Boolean).join(' ')}
        ref={ref}
      >
        <ShopCardPlanTileMedia
          className={styles.media}
          onClick={() => setShowPlanSelect(false)}
        >
          {imgSrc && (
            <CustomImage
              src={imgSrc}
              alt={title}
              height={448}
              width={300}
              style={{
                width: '100%',
                objectFit: 'cover',
                borderRadius: '12px',
              }}
            />
          )}
        </ShopCardPlanTileMedia>
        <ShopCardBody>
          <ShopCardSpecSection className={`m1spec ${styles.m1spec}`}>
            <h3 className="title">
              <CustomLink href="/pricing" target="_blank">
                {title}
              </CustomLink>
            </h3>
            {description && <p className="description">{description}</p>}
            <br />
          </ShopCardSpecSection>
          {outOfStock ? (
            <ShopCardTag>{t('ring.shop.soldOut')}</ShopCardTag>
          ) : null}
          <ShopCardFooterRow
            aria-hidden={showPlanSelect}
            style={showPlanSelect ? { visibility: 'hidden' } : undefined}
          >
              <ShopCardPriceCluster className="pricing-info">
                {hasManyPlans && (
                  <span style={{ flexBasis: '100%' }}>
                    {tCart('sharedCart.upsell.startsFrom')}
                  </span>
                )}
                {showStrike && (
                  <span className="striked">
                    {currency}
                    {formatPriceWithComma(quoted.list)}
                  </span>
                )}
                <span data-testid={oneTime ? 'm1-otp-price' : 'm1-sub-price'}>
                  {currency}
                  {formatPriceWithComma(quoted.sale)}
                </span>
                <span>{resolvedTaxString}</span>
              </ShopCardPriceCluster>
              <ShopCardPrimaryCta
                className={
                  isLoading || outOfStock ? 'quickAdd disable' : 'quickAdd'
                }
                data-product={title}
                data-buttontype="add to cart"
                onClick={handleButtonClick}
                disabled={outOfStock || isLoading}
                aria-busy={isLoading}
              >
                {isLoading ? (
                  <DotSpinner color="#FFFFFF" />
                ) : directCheckout ? (
                  tCart('sharedCart.upsell.buyNow')
                ) : (
                  tCart('sharedCart.upsell.addToCart')
                )}
              </ShopCardPrimaryCta>
          </ShopCardFooterRow>
        </ShopCardBody>

        {hasManyPlans && (
          <ShopCardMediaDimBackdrop
            className={showPlanSelect ? 'open' : ''}
            onClick={closePlanSelect}
          />
        )}
        {hasManyPlans && (
          <ShopCardBottomSheet
            className={`sizeOverlay ${showPlanSelect ? 'open' : ''} ${styles.bottomSheet}`}
            aria-hidden={!showPlanSelect}
          >
            <div className="planSelect">
                <div className="planOptions">
                  {planEntries.map(([key, plan]) => (
                    <button
                      key={key}
                      className={`planBtn ${
                        selectedPlanKey === key ? 'selected' : ''
                      }`}
                      onClick={() => setSelectedPlanKey(key)}
                      aria-pressed={selectedPlanKey === key}
                    >
                      <span className="planLabel">
                        {plan.title || plan.displayName}
                      </span>
                      <span className="planPrice" data-testid="m1-plan-price">
                        {priceFor(plan.price) < plan.price && (
                          <span className="striked">
                            {currency}
                            {formatPriceWithComma(plan.price)}
                          </span>
                        )}
                        {currency}
                        {formatPriceWithComma(priceFor(plan.price))}
                      </span>
                      {plan.tag ? (
                        <span className="saveBadge">{plan.tag}</span>
                      ) : null}
                    </button>
                  ))}
                </div>
                <button
                  className={`addToCart ${
                    !selectedPlanKey || isLoading ? 'disabled' : ''
                  }`}
                  onClick={handleAddToCart}
                  disabled={!selectedPlanKey || isLoading}
                >
                  {isLoading ? (
                    <DotSpinner color="#fff" />
                  ) : selectedPlanKey ? (
                    <>
                      {directCheckout
                        ? t('ring.shop.buyNowFor', {
                            price: `${getCurrencySymbolFromEnum((CartClient.currency ?? Currency.usd) as Currency)}${formatPriceWithComma(priceFor(getSelectedPlan()?.price ?? 0))}`,
                          })
                        : tCart('sharedCart.upsell.addToCartFor', {
                            price: `${getCurrencySymbolFromEnum((CartClient.currency ?? Currency.usd) as Currency)}${formatPriceWithComma(priceFor(getSelectedPlan()?.price ?? 0))}`,
                          })}
                    </>
                  ) : (
                    tCart('sharedCart.upsell.selectPlan')
                  )}
                </button>
              </div>
          </ShopCardBottomSheet>
        )}
      </ShopProductCardRoot>
    );
};
